<script setup lang="ts">
import { computed, onErrorCaptured, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import DefaultLayout from '@/layouts/DefaultLayout.vue'
import AuthLayout from '@/layouts/AuthLayout.vue'
import AppToast from '@/ui/AppToast.vue'
import AppButton from '@/ui/AppButton.vue'

const { t } = useI18n()
const route = useRoute()

const layout = computed(() => (route.meta.layout === 'auth' ? AuthLayout : DefaultLayout))

/* Last-resort error boundary: a rendering error anywhere below must never
   leave the user with a blank page. */
const fatalError = ref(false)
onErrorCaptured((err) => {
  console.error('unhandled component error', err)
  fatalError.value = true
  return false
})

function reload(): void {
  window.location.reload()
}
</script>

<template>
  <div v-if="fatalError" class="fatal">
    <p>{{ t('app.errorFallback') }}</p>
    <AppButton variant="primary" @click="reload">
      {{ t('app.reload') }}
    </AppButton>
  </div>
  <template v-else>
    <a class="skip-link" href="#main">{{ t('app.skipToContent') }}</a>
    <component :is="layout">
      <router-view />
    </component>
    <AppToast />
  </template>
</template>

<style scoped>
.fatal {
  display: grid;
  place-content: center;
  gap: var(--space-4);
  min-height: 100vh;
  text-align: center;
}
</style>
